// @file Team permissions Members settings store
import { captureFetchException } from '@@/bits/error_tracker'
import { __ } from '@@/bits/intl'
import {
  createRetroactiveWallUpdatesConfirmationDialog,
  willTriggerRetroactiveWallUpdatesV2,
} from '@@/bits/permissions_settings_helper'
import PromiseQueue from '@@/bits/promise_queue'
import { LibraryPermissions as LibraryPermissionsApi } from '@@/dashboard/padlet_api'
import type { WallAccessDefault } from '@@/enums'
import {
  AllowedDefaultWallRole,
  LibraryType,
  SnackbarNotificationType,
  TeamPermissionString,
  WallAccessRule,
} from '@@/enums'
import { useGlobalConfirmationDialogStore } from '@@/pinia/global_confirmation_dialog'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useWindowSizeStore } from '@@/pinia/window_size'
import type { Library, TeamLibraryMemberPermission, TeamLibraryPermission } from '@@/types'
import type { JsonAPIResource } from '@padlet/arvo'
import { defineStore } from 'pinia'
import { computed, ref, shallowReactive } from 'vue'

enum TeamPermissionsMembersStatus {
  Loading = 'Loading',
  Completed = 'Completed',
  Errored = 'Errored',
}

// Team member permissions include both boolean and string fields
type TeamMemberPermission = TeamLibraryMemberPermission

const queue = new PromiseQueue()
export const useSettingsTeamPermissionsMembersStore = defineStore('settingsTeamPermissionsMembers', () => {
  const globalSnackbarStore = useGlobalSnackbarStore()

  // State - focused on V2 access control settings
  const permissionsState = shallowReactive<TeamMemberPermission>({
    listedSetting: WallAccessRule.Allowed,
    publishableSetting: WallAccessRule.Allowed,
    requireLogin: WallAccessRule.Allowed,
    requirePassword: WallAccessRule.Allowed,
    allowVisitorAccess: WallAccessRule.Allowed,
    allowMemberAccess: WallAccessRule.Allowed,
    defaultWallVisitorRole: AllowedDefaultWallRole.Commenter,
    defaultWallMemberRole: AllowedDefaultWallRole.Commenter,
  })

  const teamPermissionsMembersStatus = ref(TeamPermissionsMembersStatus.Loading)
  const library = ref<Library>()
  const isTeamLibrary = computed(() => library.value?.libraryType === LibraryType.Team)
  const isTeamPermissionsMembersLoading = computed(
    () => teamPermissionsMembersStatus.value === TeamPermissionsMembersStatus.Loading,
  )

  // Actions
  function setPermissionsState(permissions: TeamLibraryMemberPermission): void {
    for (const permission in permissionsState) {
      permissionsState[permission] = permissions[permission] ?? permissionsState[permission]
    }
  }

  async function initialize(libraryObject?: Library): Promise<void> {
    if (libraryObject === undefined) {
      void globalSnackbarStore.genericFetchError()
      return
    }
    library.value = libraryObject
    await fetchTeamPermissionsMembers(libraryObject.id)
  }

  async function fetchTeamPermissionsMembers(libraryId: number): Promise<void> {
    try {
      teamPermissionsMembersStatus.value = TeamPermissionsMembersStatus.Loading

      const response = await LibraryPermissionsApi.fetch(libraryId)
      // For team libraries, we'll use the member permission structure
      const permissions = (response.data as JsonAPIResource<TeamLibraryPermission>)?.attributes.settings?.member

      if (permissions != null) setPermissionsState(permissions)
      teamPermissionsMembersStatus.value = TeamPermissionsMembersStatus.Completed
    } catch (e) {
      void globalSnackbarStore.genericFetchError()
      teamPermissionsMembersStatus.value = TeamPermissionsMembersStatus.Errored
      captureFetchException(e, { source: 'SettingsTeamPermissionsMembersFetchPermissions' })
    }
  }

  async function updateV2AccessSetting(
    key: TeamPermissionString,
    newSetting: WallAccessRule | WallAccessDefault | AllowedDefaultWallRole,
    initialSetting: WallAccessRule | WallAccessDefault | AllowedDefaultWallRole,
  ): Promise<void> {
    const libraryId = library.value?.id as number
    try {
      const response = await LibraryPermissionsApi.update(libraryId, {
        member: { [key]: newSetting },
      })
      const permissions = (response.data as JsonAPIResource<TeamLibraryPermission>)?.attributes.settings?.member
      if (permissions != null) {
        setPermissionsState(permissions)
      }
    } catch (e) {
      void globalSnackbarStore.setSnackbar({
        message: __('Error updating permission.'),
        notificationType: SnackbarNotificationType.error,
      })
      queue.clear()
      permissionsState[key] = initialSetting
      captureFetchException(e, { source: 'SettingsTeamPermissionsMembersUpdateV2AccessSetting' })
    }
  }

  async function queueUpdateV2AccessSetting(
    key: TeamPermissionString,
    setting: WallAccessRule | WallAccessDefault | AllowedDefaultWallRole,
  ): Promise<void> {
    const initialSetting = permissionsState[key] as WallAccessRule | WallAccessDefault | AllowedDefaultWallRole

    // Check if this permission change will trigger retroactive wall updates
    if (willTriggerRetroactiveWallUpdates(key, setting)) {
      showRetroactiveWallUpdatesConfirmation(key, setting, initialSetting)
      return
    }

    // Proceed with the update directly if no retroactive updates needed
    proceedWithPermissionUpdate(key, setting, initialSetting)
  }

  function willTriggerRetroactiveWallUpdates(
    key: TeamPermissionString,
    setting: WallAccessRule | WallAccessDefault | AllowedDefaultWallRole,
  ): boolean {
    // These are the access control settings V2 permissions that trigger retroactive updates
    const accessControlV2Permissions = [
      TeamPermissionString.ListedSetting,
      TeamPermissionString.PublishableSetting,
      TeamPermissionString.RequirePassword,
      TeamPermissionString.RequireLogin,
      TeamPermissionString.AllowVisitorAccess,
      TeamPermissionString.AllowMemberAccess,
    ]

    return willTriggerRetroactiveWallUpdatesV2(key, setting, accessControlV2Permissions)
  }

  function showRetroactiveWallUpdatesConfirmation(
    key: TeamPermissionString,
    setting: WallAccessRule | WallAccessDefault | AllowedDefaultWallRole,
    initialSetting: WallAccessRule | WallAccessDefault | AllowedDefaultWallRole,
  ): void {
    const globalConfirmationDialogStore = useGlobalConfirmationDialogStore()
    const dialogConfig = createRetroactiveWallUpdatesConfirmationDialog({
      userType: 'member',
      isPhone: useWindowSizeStore().isPhone,
      afterConfirmAction: () => proceedWithPermissionUpdate(key, setting, initialSetting),
      permissionKey: key,
      settingValue: setting,
    })
    globalConfirmationDialogStore.openConfirmationDialog(dialogConfig)
  }

  function proceedWithPermissionUpdate(
    key: TeamPermissionString,
    setting: WallAccessRule | WallAccessDefault | AllowedDefaultWallRole,
    initialSetting: WallAccessRule | WallAccessDefault | AllowedDefaultWallRole,
  ): void {
    permissionsState[key] = setting
    void queue.enqueue(
      'updateMemberV2AccessSetting',
      async () => await updateV2AccessSetting(key, setting, initialSetting),
    )
  }

  return {
    // State
    permissionsState,
    library,
    isTeamLibrary,
    isTeamPermissionsMembersLoading,

    // Actions
    initialize,
    queueUpdateV2AccessSetting,
  }
})
